<script setup lang="ts">
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import FileReport from "@/components/FileReport.vue";
import { ref, onMounted } from "vue";

onMounted(() => {
  document.title = "ElevatED – About";
});

// Import background image for better Vite handling
import aboutBgUrl from "/about-bg.png";

// FAQ state management
const expandedFAQ = ref<number | null>(0); // Start with first FAQ expanded

const toggleFAQ = (index: number) => {
  if (expandedFAQ.value === index) {
    expandedFAQ.value = null; // Collapse if already expanded
  } else {
    expandedFAQ.value = index; // Expand this FAQ
  }
};
</script>

<template>
  <main class="flex-1 min-h-[60vh] md:min-h-[80vh] relative bg-white">
    <FileReport />

    <!-- Background with Header and Hero Section -->
    <div
      class="hero-bg w-full min-h-screen relative"
      :style="{ backgroundImage: `url(${aboutBgUrl})` }"
    >
      <div class="about-page min-h-screen w-full relative flex flex-col bg-transparent">
        <Header />

        <!-- Hero Section with Image and Floating Text -->
        <div class="hero-section flex-1 relative">
          <!-- Mobile Layout: Stack text above image -->
          <div class="mobile-layout block md:hidden px-6">
            <!-- Text content for mobile (above image) -->
            <div class="mobile-text-content mb-8">
              <div
                class="bg-white rounded-3xl p-8 shadow-lg border-2 border-black mx-auto w-full max-w-[90%]"
              >
                <h2 class="text-[1.75rem] font-black text-black mb-4 text-center leading-tight">
                  About ElevatED
                </h2>
                <p class="text-base text-black leading-relaxed text-center mb-3">
                  We're on a mission to make studying easier and more effective for every SHS
                  student in the Philippines!
                </p>
                <p class="text-sm font-bold text-black text-center italic">
                  No more struggling alone!
                </p>
              </div>
            </div>

            <!-- Image for mobile (below text) -->
            <div class="mobile-image-container mb-6">
              <div
                class="rounded-3xl overflow-hidden shadow-xl border-2 border-black bg-white mx-auto w-full max-w-[90%]"
              >
                <picture>
                  <source srcset="/about-img.webp" type="image/webp" />
                  <img
                    src="/about-img.png"
                    alt="Students studying together"
                    class="w-full h-auto object-cover"
                    width="1200"
                    height="800"
                    loading="eager"
                  />
                </picture>
              </div>
            </div>
          </div>

          <!-- Desktop/Tablet Layout: Floating text overlay -->
          <div class="desktop-layout hidden md:block">
            <!-- Main Image Container -->
            <div
              class="image-container relative w-full h-full flex items-center justify-center pt-8 md:pt-16"
            >
              <!-- Large Rounded Rectangle with Image -->
              <div class="main-image-wrapper relative w-[100%] max-w-5xl mx-auto">
                <!-- Rounded rectangle background -->
                <div class="rounded-3xl overflow-hidden shadow-2xl border-3 border-black bg-white">
                  <picture>
                    <source srcset="/about-img.png" type="image/png" />
                    <img
                      src="/about-img.png"
                      alt="Students studying together"
                      class="w-full h-auto object-cover"

                      loading="eager"
                      fetchpriority="high"
                    />
                  </picture>
                </div>

                <!-- Floating description container -->
                <div class="floating-description absolute top-8 right-8 lg:-right-8 lg:top-12">
                  <div class="relative">
                    <!-- Background image for the floating text -->
                    <picture>
                      <source srcset="/about-descrip.webp" type="image/webp" />
                      <img
                        src="/about-descrip.png"
                        alt="Description background"
                        class="w-full h-auto max-w-[400px] lg:max-w-[500px]"
                        width="500"
                        height="300"
                        loading="lazy"
                      />
                    </picture>
                    <!-- Text overlay -->
                    <div
                      class="text-overlay absolute inset-3 flex flex-col justify-center items-start p-8"
                    >
                      <div class="text-content">
                        <h2 class="text-3xl lg:text-3xl font-black text-black mb-2 pt-4">
                          About ElevatED
                        </h2>
                        <p class="text-base lg:text-medium font-small text-black leading-relaxed">
                          We're on a mission to make studying easier and more effective for every
                          SHS student in the Philippines!
                        </p>
                        <p class="text-sm lg:text-base font-bold text-black mt-1 italic">
                          No more struggling alone!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Our Story Section -->
      <div class="story-section w-full pt-8 pb-12 md:pt-12 md:pb-20">
        <div class="container mx-auto px-4 md:px-8 max-w-7xl">
          <!-- Section Title -->
          <div class="title-section text-center mb-10 md:mb-16">
            <div
              class="flex items-baseline justify-center gap-2 md:gap-4 lg:gap-8 mb-6 md:mb-8 flex-wrap"
            >
              <!-- Our Story Title -->
              <div
                class="title-container flex items-baseline flex-wrap justify-center story-container"
              >
                <span class="text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-black"
                  >Our</span
                >
                <div class="story-italic-text relative inline-block ml-1 md:ml-4">
                  <span
                    class="relative z-10 text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-semibold italic text-white px-3 md:px-6 lg:px-8 py-1 md:py-2"
                  >
                    Story
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Story Cards Grid -->
          <div
            class="cards-grid flex flex-col items-center gap-6 md:gap-10 lg:gap-12 max-w-7xl mx-auto px-4 mb-6"
          >
            <!-- Row 1: The Problem We Saw and Our Big Idea -->
            <div
              class="flex flex-col lg:flex-row gap-6 md:gap-8 lg:gap-12 w-full justify-center max-w-5xl"
            >
              <!-- The Problem We Saw -->
              <div
                class="story-card bg-white rounded-3xl p-6 md:p-7 lg:p-8 shadow-lg border-4 border-black w-full max-w-[90%] md:max-w-md mx-auto lg:mx-0 flex-1"
              >
                <div class="icon-container mb-4 md:mb-6 flex justify-center">
                  <img
                    src="/mind.png"
                    alt="Mind Icon"
                    class="w-12 h-12 md:w-16 md:h-16 object-contain"
                  />
                </div>
                <h3
                  class="text-lg md:text-xl lg:text-2xl font-black text-black text-center mb-3 md:mb-4"
                >
                  The Problem We Saw
                </h3>
                <p
                  class="text-xs md:text-sm lg:text-base text-gray-700 text-center leading-relaxed"
                >
                  Too many students fall behind simply because they can't find the right person to
                  explain things their way. Traditional tutoring is often expensive, hard to access,
                  or just not built for today's students.
                </p>
              </div>

              <!-- Our Big Idea -->
              <div
                class="story-card bg-white rounded-3xl p-6 md:p-7 lg:p-8 shadow-lg border-4 border-black w-full max-w-[90%] md:max-w-md mx-auto lg:mx-0 flex-1"
              >
                <div class="icon-container mb-4 md:mb-6 flex justify-center">
                  <img
                    src="/bulb.png"
                    alt="Bulb Icon"
                    class="w-12 h-12 md:w-16 md:h-16 object-contain"
                  />
                </div>
                <h3
                  class="text-lg md:text-xl lg:text-2xl font-black text-black text-center mb-3 md:mb-4"
                >
                  Our Big Idea
                </h3>
                <p
                  class="text-xs md:text-sm lg:text-base text-gray-700 text-center leading-relaxed"
                >
                  What if finding help felt as easy as chatting with a friend? ElevatED connects
                  students with tutors who match their needs, learning styles, and schedules—all in
                  one smart, student-centered platform.
                </p>
              </div>
            </div>

            <!-- Row 2: Our Mission (centered) -->
            <div
              class="story-card bg-white rounded-3xl p-6 md:p-7 lg:p-8 shadow-lg border-4 border-black w-full"
            >
              <div class="icon-container mb-4 md:mb-6 flex justify-center">
                <img
                  src="/rocket-ship.png"
                  alt="Rocket Ship Icon"
                  class="w-12 h-12 md:w-16 md:h-16 object-contain"
                />
              </div>
              <h3
                class="text-lg md:text-xl lg:text-2xl font-black text-black text-center mb-3 md:mb-4"
              >
                Our Mission
              </h3>
              <p class="text-xs md:text-sm lg:text-base text-gray-700 text-center leading-relaxed">
                To create an inclusive space where SHS students connect with competent,
                compassionate tutors—fostering collaboration, mentorship, and holistic growth. We're
                building the future of education, one session at a time!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div
      id="faq-section"
      class="w-full bg-[url('/homepage-bg2.webp')] bg-no-repeat bg-center"
      style="background-attachment: scroll; background-size: 100% auto; background-position: center top;"
    >
      <div class="faq-section w-full pt-10 pb-8 md:pt-16 md:pb-12 lg:pt-24 lg:pb-16">
        <div class="container mx-auto px-4 md:px-8 max-w-6xl">
          <!-- Section Title -->
          <div class="text-center mb-8 md:mb-10 lg:mb-16">
            <h2
              class="text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-black mb-4 md:mb-6 lg:mb-8"
            >
              Quick FAQs
            </h2>
          </div>

          <!-- FAQ Items -->
          <div class="faq-container max-w-4xl mx-auto space-y-4 md:space-y-5 lg:space-y-6">
            <!-- FAQ Item 1 -->
            <div
              class="faq-item bg-white rounded-2xl md:rounded-3xl border-2 md:border-4 border-black shadow-lg overflow-hidden"
            >
              <div
                class="faq-header flex items-center justify-between p-4 md:p-6 cursor-pointer"
                @click="toggleFAQ(0)"
              >
                <div class="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                  <div
                    class="w-10 h-10 md:w-12 md:h-12 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0"
                  >
                    <span class="text-black text-lg md:text-xl font-bold">?</span>
                  </div>
                  <h3 class="text-sm md:text-lg lg:text-xl font-black text-black leading-tight">
                    Is ElevatED really free to use?
                  </h3>
                </div>
                <div
                  class="w-6 h-6 md:w-8 md:h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 ml-2"
                >
                  <span class="text-gray-600 text-sm md:text-base">{{
                    expandedFAQ === 0 ? "▲" : "▼"
                  }}</span>
                </div>
              </div>
              <div v-if="expandedFAQ === 0" class="faq-content px-4 md:px-6 pb-4 md:pb-6">
                <div class="md:ml-16">
                  <p class="text-sm md:text-base text-gray-700 pt-6 pb-2 leading-relaxed">
                    Yes! ElevatED is completely free for all Senior High School students. Our tutors
                    are fellow students, teachers, and volunteers who want to help you succeed
                    without any cost.
                  </p>
                </div>
              </div>
            </div>

            <!-- FAQ Item 2 -->
            <div
              class="faq-item bg-white rounded-2xl md:rounded-3xl border-2 md:border-4 border-black shadow-lg overflow-hidden"
            >
              <div
                class="faq-header flex items-center justify-between p-4 md:p-6 cursor-pointer"
                @click="toggleFAQ(1)"
              >
                <div class="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                  <div
                    class="w-10 h-10 md:w-12 md:h-12 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0"
                  >
                    <span class="text-black text-lg md:text-xl font-bold">?</span>
                  </div>
                  <h3 class="text-sm md:text-lg lg:text-xl font-black text-black leading-tight">
                    How do I find a tutor for a subject?
                  </h3>
                </div>
                <div
                  class="w-6 h-6 md:w-8 md:h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 ml-2"
                >
                  <span class="text-gray-600 text-sm md:text-base">{{
                    expandedFAQ === 1 ? "▲" : "▼"
                  }}</span>
                </div>
              </div>
              <div v-if="expandedFAQ === 1" class="faq-content px-4 md:px-6 pb-4 md:pb-6">
                <div class="md:ml-16">
                  <p class="text-sm md:text-base text-gray-700 pt-6 pb-2 leading-relaxed">
                    Simply browse our tutor profiles by subject, read reviews from other students,
                    and choose someone who matches your learning style and schedule. You can also
                    use our smart matching system to find the perfect tutor for you!
                  </p>
                </div>
              </div>
            </div>

            <!-- FAQ Item 3 -->
            <div
              class="faq-item bg-white rounded-2xl md:rounded-3xl border-2 md:border-4 border-black shadow-lg overflow-hidden"
            >
              <div
                class="faq-header flex items-center justify-between p-4 md:p-6 cursor-pointer"
                @click="toggleFAQ(2)"
              >
                <div class="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                  <div
                    class="w-10 h-10 md:w-12 md:h-12 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0"
                  >
                    <span class="text-black text-lg md:text-xl font-bold">?</span>
                  </div>
                  <h3 class="text-sm md:text-lg lg:text-xl font-black text-black leading-tight">
                    What if I don't like my tutor or the session?
                  </h3>
                </div>
                <div
                  class="w-6 h-6 md:w-8 md:h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 ml-2"
                >
                  <span class="text-gray-600 text-sm md:text-base">{{
                    expandedFAQ === 2 ? "▲" : "▼"
                  }}</span>
                </div>
              </div>
              <div v-if="expandedFAQ === 2" class="faq-content px-4 md:px-6 pb-4 md:pb-6">
                <div class="md:ml-16">
                  <p class="text-sm md:text-base text-gray-700 pt-6 pb-2 leading-relaxed">
                    No worries! You can easily switch to a different tutor anytime. We want you to
                    find the perfect match, so feel free to try different tutors until you find
                    someone who clicks with your learning style.
                  </p>
                </div>
              </div>
            </div>

            <!-- FAQ Item 4 -->
            <div
              class="faq-item bg-white rounded-2xl md:rounded-3xl border-2 md:border-4 border-black shadow-lg overflow-hidden"
            >
              <div
                class="faq-header flex items-center justify-between p-4 md:p-6 cursor-pointer"
                @click="toggleFAQ(3)"
              >
                <div class="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                  <div
                    class="w-10 h-10 md:w-12 md:h-12 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0"
                  >
                    <span class="text-black text-lg md:text-xl font-bold">?</span>
                  </div>
                  <h3 class="text-sm md:text-lg lg:text-xl font-black text-black leading-tight">
                    How do I schedule a tutoring session?
                  </h3>
                </div>
                <div
                  class="w-6 h-6 md:w-8 md:h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 ml-2"
                >
                  <span class="text-gray-600 text-sm md:text-base">{{
                    expandedFAQ === 3 ? "▲" : "▼"
                  }}</span>
                </div>
              </div>
              <div v-if="expandedFAQ === 3" class="faq-content px-4 md:px-6 pb-4 md:pb-6">
                <div class="md:ml-16">
                  <p class="text-sm md:text-base text-gray-700 pt-6 pb-2 leading-relaxed">
                    Once you've found a tutor you like, you can view their available time slots and
                    schedule an appointment directly through our platform. You'll get a confirmation and
                    reminder before your session starts.
                  </p>
                </div>
              </div>
            </div>

            <!-- FAQ Item 5 -->
            <div
              class="faq-item bg-white rounded-2xl md:rounded-3xl border-2 md:border-4 border-black shadow-lg overflow-hidden"
            >
              <div
                class="faq-header flex items-center justify-between p-4 md:p-6 cursor-pointer"
                @click="toggleFAQ(4)"
              >
                <div class="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                  <div
                    class="w-10 h-10 md:w-12 md:h-12 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0"
                  >
                    <span class="text-black text-lg md:text-xl font-bold">?</span>
                  </div>
                  <h3 class="text-sm md:text-lg lg:text-xl font-black text-black leading-tight">
                    Can I become a tutor too?
                  </h3>
                </div>
                <div
                  class="w-6 h-6 md:w-8 md:h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 ml-2"
                >
                  <span class="text-gray-600 text-sm md:text-base">{{
                    expandedFAQ === 4 ? "▲" : "▼"
                  }}</span>
                </div>
              </div>
              <div v-if="expandedFAQ === 4" class="faq-content px-4 md:px-6 pb-4 md:pb-6">
                <div class="md:ml-16">
                  <p class="text-sm md:text-base text-gray-700 pt-6 pb-2 leading-relaxed">
                    Absolutely! If you're good at a subject and want to help fellow students, you
                    can apply to become a tutor. We'll verify your qualifications and help you get
                    started with your first students.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Us Section -->
    <div class="contact-section w-full bg-white pt-8 pb-10 md:pt-12 md:pb-20 lg:pt-16 lg:pb-28 mt-4 mb-8">
      <div class="container mx-auto px-4 md:px-8 max-w-5xl">
        <!-- Section Title -->
        <div class="text-center mb-6 md:mb-14 lg:mb-16">
          <div
            class="flex items-center justify-center gap-2 md:gap-4 lg:gap-8 mb-4 md:mb-8 flex-wrap"
          >
            <!-- Star decoration -->
            <div class="text-2xl md:text-4xl lg:text-6xl">⭐</div>
            <h2 class="text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-black">
              Developers
            </h2>
            <!-- Star decoration -->
            <div class="text-2xl md:text-4xl lg:text-6xl">⭐</div>
          </div>
        </div>

        <!-- Contact Cards Grid -->
        <div class="contact-grid grid grid-cols-1 gap-5 md:gap-7 max-w-4xl mx-auto px-2 md:px-0">
          <!-- Mhai Bringuelo -->
          <div
            class="contact-card bg-white rounded-2xl md:rounded-3xl p-3 md:p-4 lg:p-6 border-2 md:border-4 border-black shadow-lg"
          >
            <div class="flex items-center gap-3 md:gap-4">
              <div
                class="contact-avatar w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-gray-300 rounded-full flex-shrink-0 overflow-hidden"
              >
                <picture>
                  <source srcset="/mhai.webp" type="image/webp" />
                  <img src="/mhai.jpg" alt="Mhai Bringuelo" class="w-full h-full object-cover" width="200" height="200" loading="lazy" />
                </picture>
              </div>
              <div class="contact-info flex-1 min-w-0">
                <h3 class="text-base md:text-lg lg:text-xl font-black text-black mb-1">
                  Mhai Bringuelo
                </h3>
                <div class="contact-details space-y-0.5">
                  <p class="text-xs md:text-sm text-gray-700 break-words leading-tight">
                    mhailaine@gmail.com / mhailaine.brinquelo.cics@ust.edu.ph
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Jea Antonio -->
          <div
            class="contact-card bg-white rounded-2xl md:rounded-3xl p-3 md:p-4 lg:p-6 border-2 md:border-4 border-black shadow-lg"
          >
            <div class="flex items-center gap-3 md:gap-4">
              <div
                class="contact-avatar w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-gray-300 rounded-full flex-shrink-0 overflow-hidden"
              >
                <picture>
                  <source srcset="/jea-img.webp" type="image/webp" />
                  <img src="/jea-img.png" alt="Jea Antonio" class="w-full h-full object-cover" width="200" height="200" loading="lazy" />
                </picture>
              </div>
              <div class="contact-info flex-1 min-w-0">
                <h3 class="text-base md:text-lg lg:text-xl font-black text-black mb-1">
                  Jea Antonio
                </h3>
                <div class="contact-details space-y-0.5">
                  <p class="text-xs md:text-sm text-gray-700 break-words leading-tight">
                    jeaantonio@gmail.com / jeasofia.antonio.cics@ust.edu.ph
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Peter Arnoza -->
          <div
            class="contact-card bg-white rounded-2xl md:rounded-3xl p-3 md:p-4 lg:p-6 border-2 md:border-4 border-black shadow-lg"
          >
            <div class="flex items-center gap-3 md:gap-4">
              <div
                class="contact-avatar w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-gray-300 rounded-full flex-shrink-0 overflow-hidden"
              >
                <picture>
                  <source srcset="/peter_1x1.webp" type="image/webp" />
                  <img src="/peter_1x1.png" alt="Peter Arnoza" class="w-full h-full object-cover" width="200" height="200" loading="lazy" />
                </picture>
              </div>
              <div class="contact-info flex-1 min-w-0">
                <h3 class="text-base md:text-lg lg:text-xl font-black text-black mb-1">
                  Peter Arnoza
                </h3>
                <div class="contact-details space-y-0.5">
                  <p class="text-xs md:text-sm text-gray-700 break-words leading-tight">
                    peterarnoza@gmail.com / peterlenard.arnoza.cics@ust.edu.ph
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Bianca Tinsay -->
          <div
            class="contact-card bg-white rounded-2xl md:rounded-3xl p-3 md:p-4 lg:p-6 border-2 md:border-4 border-black shadow-lg"
          >
            <div class="flex items-center gap-3 md:gap-4">
              <div
                class="contact-avatar w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-gray-300 rounded-full flex-shrink-0 overflow-hidden"
              >
                <picture>
                  <source srcset="/bianca-img.webp" type="image/webp" />
                  <img src="/bianca-img.jpg" alt="Bianca Tinsay" class="w-full h-full object-cover" width="200" height="200" loading="lazy" />
                </picture>
              </div>
              <div class="contact-info flex-1 min-w-0">
                <h3 class="text-base md:text-lg lg:text-xl font-black text-black mb-1">
                  Bianca Tinsay
                </h3>
                <div class="contact-details space-y-0.5">
                  <p class="text-xs md:text-sm text-gray-700 break-words leading-tight">
                    biancatinsay@gmail.com / biancacharlotte.tinsay.cics@ust.edu.ph
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </main>
</template>

<style scoped>
.story-italic-text {
  font-weight: 600;
  font-family: "Playfair Display";
  font-style: italic;
  padding-left: 1rem;
  padding-right: 1rem;
}

.story-container {
  background-image: url("/story-section.png");
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  padding: 15px 25px;
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 40em;
  min-height: 80px;
}

@media (max-width: 768px) {
  .story-container {
    padding: 12px 20px;
    width: 85%;
    max-width: 22em;
    background-size: contain;
    min-height: 70px;
  }
}

@media (max-width: 480px) {
  .story-container {
    padding: 0px;
    width: 30em !important;
    height: 60px;
    background-size: contain;
    gap: 0.2rem;
  }

  .story-italic-text {
    margin-left: -1rem;
  }
  
}

.text-overlay {
  padding: 4em;
  height: 10em;
}
.hero-bg {
  background-image: url("/about-bg.webp");
  background-position: top;
  background-size: cover;
  background-repeat: no-repeat;
  background-color: #fecaca; /* Fallback color for debugging */
}

/* Fallback for browsers without WebP support */
.no-webp .hero-bg {
  background-image: url("/about-bg.png");
}

.about-page {
  background-color: transparent;
}

.hero-section {
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 50em;
}

.main-image-wrapper {
  position: relative;
  animation: fadeInUp 0.8s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.floating-description {
  animation: float 3s ease-in-out infinite;
  z-index: 10;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.text-content {
  color: #000;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

.story-section {
  background: transparent;
}

.story-card {
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
  border: 4px solid #000;
  box-shadow: 8px 8px 0px #000;
  width: 100%;
  max-width: 450px;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin: 0 auto;
}

.story-card:hover {
  transform: scale(1.03) translateY(-5px);
  box-shadow: 12px 12px 0px #000;
}

.cards-grid {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.5rem;
  width: 100%;
}

@media (min-width: 1024px) {
  .cards-grid > div:first-child {
    display: flex;
    flex-direction: row;
    gap: 2.5rem;
    justify-content: center;
    width: 100%;
    max-width: 1200px;
  }

  .cards-grid > div:first-child .story-card {
    flex: 1;
    max-width: 500px;
  }

  .story-card {
    max-width: 500px !important;
  }
}
.icon-container {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .hero-bg {
    height: auto;
    min-height: 100vh;
  }

  .about-page {
    min-height: auto;
  }

  .hero-section {
    min-height: auto;
    padding: 1.5rem 0;
  }

  .mobile-layout {
    padding-top: 3rem;
    padding-bottom: 3rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .mobile-text-content {
    margin-bottom: 1rem;
    margin-top: 2rem;
  }

  .mobile-text-content > div {
    max-width: 85% !important;
  }

  .mobile-image-container {
    margin-bottom: 1rem;
  }

  .mobile-image-container > div {
    max-width: 85% !important;
  }

  .story-section {
    padding-top: 3rem !important;
    padding-bottom: 4rem !important;
  }

  .title-section {
    margin-bottom: 2.5rem !important;
  }

  .cards-grid {
    gap: 2rem !important;
    padding: 0 1.5rem !important;
  }

  .story-card {
    min-height: auto;
    padding: 2rem !important;
    max-width: 95% !important;
    margin: 0 auto !important;
  }

  .faq-section {
    padding: 2rem 0 !important;
  }

  .faq-container {
    gap: 1rem !important;
    padding: 0 1rem !important;
  }

  .contact-section {
    padding: 2rem 0 !important;
  }

  .contact-grid {
    padding: 0 1rem !important;
  }

  /* Title container mobile fixes */
  .title-container {
    flex-direction: column !important;
    gap: 0rem !important;
    align-items: center !important;
  }

  .title-container span {
    font-size: 2.5rem !important;
    text-align: center;
  }

  .story-italic-text span {
    font-size: 2.5rem !important;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .hero-bg {
    height: auto;
    min-height: 100vh;
  }

  .mobile-layout {
    padding-top: 2rem;
    padding-bottom: 2rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .mobile-text-content > div {
    padding: 2rem 1.5rem !important;
    max-width: 92% !important;
    margin: 0 auto;
  }

  .mobile-text-content h2 {
    font-size: 1.6rem !important;
    margin-bottom: 1rem !important;
    line-height: 1.2 !important;
  }

  .mobile-text-content p {
    font-size: 0.95rem !important;
    line-height: 1.6 !important;
  }

  .mobile-text-content p:last-child {
    font-size: 0.9rem !important;
    margin-top: 0.75rem !important;
  }

  .mobile-image-container > div {
    max-width: 92% !important;
    margin: 0 auto;
  }

  .story-section {
    padding-top: 2rem !important;
    padding-bottom: 2.5rem !important;
  }

  .title-container span {
    font-size: 2rem !important;
    text-align: center;
  }

  .story-italic-text span {
    font-size: 2rem !important;
    text-align: center;
  }

  .story-italic-text {
    margin-left: 0rem !important;
  }

  .story-card {
    padding: 1.75rem !important;
    max-width: calc(100% - 1rem) !important;
    margin: 0 auto !important;
  }

  .story-card h3 {
    font-size: 1.15rem !important;
    margin-bottom: 0.75rem !important;
  }

  .story-card p {
    font-size: 0.95rem !important;
    line-height: 1.6 !important;
  }

  .faq-section {
    min-height: auto !important;
    height: auto !important;
    padding: 1.5rem 0 !important;
  }

  .faq-section h2 {
    font-size: 2rem !important;
    margin-bottom: 1.5rem !important;
  }

  .faq-item {
    margin: 0 0.5rem;
  }

  .faq-container {
    padding: 0 0.5rem !important;
  }

  .contact-section {
    padding: 2rem 0 !important;
  }

  .contact-section h2 {
    font-size: 2rem !important;
    margin-bottom: 1rem !important;
  }

  .contact-card {
    padding: 1rem !important;
    margin: 0 0.5rem;
    width: auto !important;
  }

  .contact-info h3 {
    font-size: 1.05rem !important;
    margin-bottom: 0.5rem !important;
  }

  .contact-details p {
    font-size: 0.85rem !important;
    line-height: 1.4 !important;
  }

  .contact-avatar {
    width: 2.75rem !important;
    height: 2.75rem !important;
  }

  .contact-grid {
    gap: 1rem !important;
    padding: 0 0.5rem !important;
  }

  /* Fix title overflow on very small screens */
  .title-container {
    margin: 0 1rem !important;
    padding: 0 3rem;
  }

  .story-container {
    margin: 0 auto !important;
  }
}

/* Mobile Landscape Optimizations */
@media (orientation: landscape) and (max-height: 500px) {
  .desktop-layout {
    display: block !important;
  }
  
  .mobile-layout {
    display: none !important;
  }
  
  /* Make the white container image bigger */
  .main-image-wrapper {
    width: 95% !important;
    max-width: 750px !important;
    height: auto !important;
    margin: 80px auto !important;
  }
  
  .image-container {
    min-height: 100px !important;
    padding-top: 1rem !important;
  }
  
  /* Make the "About ElevatED" text smaller */
  .floating-description {
    top: 140px !important;
    right: -10px !important;
  }
  
  .floating-description img {
    max-width: 320px !important;
  }
  
  .text-overlay {
    padding: 10px 20px 10px 40px!important;
    height: auto !important;
  }
  
  .text-overlay h2 {
    font-size: 1.0rem !important;
    margin-bottom: 0.5rem !important;
    padding-top: 8px !important;
  }
  
  .text-overlay p {
    font-size: 0.7rem !important;
    line-height: 1.3 !important;
    margin-bottom: 0.25rem !important;
  }
  
  .text-overlay p:last-child {
    font-size: 0.75rem !important;
    margin-top: 0.25rem !important;
  }
  
  /* Make "Our Story" fit in one line */
  .title-container {
    display: inline !important;
    white-space: nowrap !important;
  }
  
  .title-container span {
    font-size: 1.5rem !important;
    display: inline !important;
    white-space: nowrap !important;
  }
  
  .story-italic-text {
    margin-left: 0.rem !important;
    display: inline !important;
  }
  
  .story-italic-text span {
    font-size: 1.5rem !important;
    display: inline !important;
    white-space: nowrap !important;
  }
  
  .story-section {
    padding: 1rem 0 !important;
  }
  
  .title-section {
    margin-bottom: 1rem !important;
  }
}

/* Larger Mobile Landscape - Pixel 7, iPhone XR, etc. */
@media (orientation: landscape) and (min-width: 800px) and (max-height: 500px) {
  .title-container span {
    font-size: 3.2rem !important;
  }
  
  .story-italic-text span {
    font-size: 3.2rem !important;
  }
}

/* Tablet optimizations */
@media (min-width: 768px) and (max-width: 1024px) {
  .floating-description {
    top: 1.5rem;
    right: 1rem;
  }

  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  .story-card:nth-child(3) {
    grid-column: span 2;
    max-width: 400px;
    margin: 0 auto;
  }
}

/* Large screen optimizations */
@media (min-width: 1024px) {
  .floating-description {
    top: 3rem;
    right: -2rem;
  }

  .main-image-wrapper {
    max-width: 75rem;
  }
}

/* Ensure proper spacing and alignment */
.title-container {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

.title-container .relative {
  position: relative;
  display: inline-block;
}

/* Text brand color utility */
.text-brand {
  color: #5271ff;
}

/* Responsive text scaling */
@media (max-width: 640px) {
  .title-container span {
    font-size: 2.25rem !important;
  }
}

@media (min-width: 641px) and (max-width: 768px) {
  .title-container span {
    font-size: 3rem !important;
  }
}

/* Updated Cards Grid Layout for exact image matching */
.cards-grid {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

@media (max-width: 768px) {
  .cards-grid {
    gap: 1.5rem !important;
    padding: 0 1rem !important;
  }
}

@media (max-width: 480px) {
  .cards-grid {
    gap: 1rem !important;
    padding: 0 0.5rem !important;
  }
}

@media (min-width: 1024px) {
  .cards-grid > div:first-child {
    display: flex;
    flex-direction: row;
    gap: 2rem;
    justify-content: center;
    width: 100%;
    max-width: 900px;
  }

  .cards-grid > div:first-child .story-card {
    flex: 1;
    max-width: 400px;
  }
}

/* FAQ Section Styles */
.faq-section {
  background: transparent;
  height: auto !important;
}

@media (max-width: 768px) {
  .faq-section {
    padding: 0.5rem 0 !important;
  }
}

@media (max-width: 480px) {
  .faq-section {
    padding: 0.5rem 0 !important;
  }
}

.faq-container {
  display: flex;
  flex-direction: column;
}

.faq-item {
  background: white;
}

.faq-item:hover {
  transform: translateY(-2px);
  box-shadow: 10px 10px 0px #000;
  transition: all 0.3s ease;
}

.faq-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.faq-header:hover {
  background-color: #f8fafc;
}

.faq-content {
  border-top: 1px solid #e5e7eb;
  animation: fadeInDown 0.3s ease-out;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.faq-header span {
  transition: transform 0.3s ease;
}

/* Contact Us Section Styles */
.contact-section {
  background: linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%);
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr;
  max-width: 800px;
  margin: 0 auto;
}

.contact-card {
  transition: all 0.3s ease;
  background: white;
  width: 100%;
}

.contact-card:hover {
  transform: translateY(-3px);
  box-shadow: 10px 10px 0px #000;
}

.contact-avatar {
  background: linear-gradient(135deg, #e5e7eb, #d1d5db);
  border: 2px solid #9ca3af;
}

.contact-info h3 {
  color: #000;
  font-weight: 900;
  line-height: 1.2;
}

.contact-details p {
  color: #374151;
  line-height: 1.4;
}
</style>
